/**
 * HCD / LCD — High-Close-Doji and Low-Close-Doji reversal bars. SweeterTrade's
 * rule (2026-11-02), solved on their exported `hcdLcd` column: META daily agrees
 * on every bar after the warm-up, NVDA daily on all but 13 of 6,687 and NVDA
 * weekly on all but 2 of 1,350, each a rounding tie in their four-decimal prices.
 *
 *   doji(j)    = |open − close| < (high − low) · 0.08
 *   HCD at i (+1), through a doji j one, two or three bars back:
 *     close[i] > high[j], every close between them (j+1 .. i−1) below high[j],
 *     and close[j] below the highest close of the 5 bars before j
 *   LCD at i (−1), the mirror:
 *     close[i] < low[j], every close between them above low[j],
 *     and close[j] above the lowest close of the 7 bars before j
 *
 * Each doji is armed both ways. A close between them that only touches the
 * doji's high (or low) spends it, and so does the bar that fires: the next bar
 * reads that close as one between, so the same doji does not fire twice. The
 * context test keeps a doji at a 5-bar closing high from calling an HCD, and one
 * at a 7-bar closing low from calling an LCD. A bar cannot be an HCD through one
 * doji and an LCD through another while every low <= close <= high: the newer
 * doji's close is one between for the older, which puts it on the wrong side of
 * this close. Bars that break that are read as neither. It is a mark on the
 * bar, not a held state.
 *
 * These are sparse fire signals, exposed as HCD fire + age and LCD fire + age
 * in each of a daily, weekly, and monthly timeframe (ISO-week / calendar-month
 * OHLC; fires on the period-close bar of the current in-progress period).
 *
 * Until 2026-11-02 this was the MPP C++ `AfiatEquiScan_HLCD_Function`: a doji
 * one or two bars back, and a close beyond the prior bar's high or low.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DWM, TF_PARAM } from "./timeframe";
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";
import { ageSeries } from "./age";
import { resampleOHLC, htIndexPerDay, type HtFreq } from "./weekly";

const DOJI_PCT = 8; // body < 8% of the bar's range counts as a doji (ratio 0.08)

/** The one configurable HLCD input — how small a candle body (as a % of the
 *  bar's range) counts as a doji. Default 8% reproduces the canonical HLCD. */
export const HLCD_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "dojiPct", label: "Doji body %", kind: "number", default: DOJI_PCT, min: 1, max: 50, step: 1 },
];
/** Body/range ratio from the params object (default 0.08). */
const ratioOf = (p: IndicatorParams = {}) => {
  const pct = typeof p.dojiPct === "number" && Number.isFinite(p.dojiPct) ? p.dojiPct : DOJI_PCT;
  return Math.max(0.001, pct / 100);
};

function isDoji(open: number, high: number, low: number, close: number, ratio = DOJI_PCT / 100): boolean {
  return Math.abs(open - close) < (high - low) * ratio;
}

const DOJI_REACH = 3; // a doji up to this many bars back can call the reversal
const HCD_CONTEXT = 5; // an HCD's doji closes below the highest close of this many bars before it
const LCD_CONTEXT = 7; // an LCD's doji closes above the lowest close of this many bars before it

/** Highest (`sign` 1) or lowest (`sign` −1) close of the `len` bars before `j`,
 *  or null when there are not that many. */
function closeExtremeBefore(closes: ReadonlyArray<number>, j: number, len: number, sign: 1 | -1): number | null {
  if (j < len) return null;
  let x = closes[j - 1]!;
  for (let k = j - len; k < j - 1; k++) {
    const c = closes[k]!;
    if (sign > 0 ? c > x : c < x) x = c;
  }
  return x;
}

/** Per-bar signed HLCD: +1 HCD, −1 LCD, 0 none. */
export function computeHlcd(
  opens: ReadonlyArray<number>,
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  ratio = DOJI_PCT / 100,
): number[] {
  const n = closes.length;
  const out = new Array<number>(n).fill(0);
  const doji = closes.map((c, j) => isDoji(opens[j]!, highs[j]!, lows[j]!, c, ratio));
  for (let i = 1; i < n; i++) {
    const c = closes[i]!;
    let hcd = false, lcd = false;
    for (let j = i - 1; j >= Math.max(1, i - DOJI_REACH); j--) {
      if (!doji[j]) continue;
      const hi = highs[j]!, lo = lows[j]!;
      let below = true, above = true; // every close between j and i below hi / above lo
      for (let k = j + 1; k < i; k++) {
        if (!(closes[k]! < hi)) below = false;
        if (!(closes[k]! > lo)) above = false;
      }
      if (c > hi && below) {
        const top = closeExtremeBefore(closes, j, HCD_CONTEXT, 1);
        if (top != null && closes[j]! < top) hcd = true;
      }
      if (c < lo && above) {
        const bottom = closeExtremeBefore(closes, j, LCD_CONTEXT, -1);
        if (bottom != null && closes[j]! > bottom) lcd = true;
      }
    }
    out[i] = hcd && !lcd ? 1 : lcd && !hcd ? -1 : 0;
  }
  return out;
}

/** 1 on bars where the signed value equals `target` (+1 HCD / −1 LCD), else 0. */
const fireOf = (signed: number[], target: 1 | -1): (number | null)[] =>
  signed.map((v) => (v === target ? 1 : 0));

/** Higher-timeframe (week / month) fire: project the HTF HLCD onto the daily bar
 *  where each HCD/LCD period first completes (lookahead-off), then 0 elsewhere.
 *  Inclusive of the in-progress period (running OHLC), so the current week/month
 *  is evaluated live rather than waiting for it to close. */
function htFire(data: OHLCV, target: 1 | -1, freq: HtFreq, ratio = DOJI_PCT / 100): (number | null)[] {
  const { opens, highs, lows, closes, times } = data;
  const n = closes.length;
  if (!opens || !highs || !lows || !times || times.length !== n) return new Array(n).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  if (h.c.length < 3) return new Array(n).fill(null);
  const hSigned = computeHlcd(h.o, h.h, h.l, h.c, ratio);
  const idxPerDay = htIndexPerDay(times, h.t);
  const out: (number | null)[] = new Array(n).fill(0);
  let prevIdx = -1;
  for (let i = 0; i < n; i++) {
    const idx = idxPerDay[i]!;
    if (idx >= 0 && idx !== prevIdx && hSigned[idx] === target) out[i] = 1;
    prevIdx = idx;
  }
  return out;
}

function dailyFire(data: OHLCV, target: 1 | -1, ratio = DOJI_PCT / 100): (number | null)[] {
  const { opens, highs, lows, closes } = data;
  if (!opens || !highs || !lows) return new Array(closes.length).fill(null);
  return fireOf(computeHlcd(opens, highs, lows, closes, ratio), target);
}

/** Age of the last HCD/LCD fire on an already-resampled series (months or
 *  weeks). Used by the scanner when the monthly buckets come from the deep
 *  store rather than the 760-day daily window. */
export function lastHlcdAge(
  bars: { o: ReadonlyArray<number>; h: ReadonlyArray<number>; l: ReadonlyArray<number>; c: ReadonlyArray<number> },
  target: 1 | -1,
  ratio = DOJI_PCT / 100,
): number | null {
  if (bars.c.length < 3) return null;
  const age = ageSeries(fireOf(computeHlcd(bars.o, bars.h, bars.l, bars.c, ratio), target), "fire");
  const v = age[age.length - 1];
  return v != null && Number.isFinite(v) ? v : null;
}

/** Higher-timeframe HCD/LCD age in HTF bars: fire is computed per period (ISO
 *  week / calendar month), aged on the HTF series (bars-since the last fire),
 *  then projected onto daily bars by the period each day belongs to (inclusive
 *  of the in-progress period). */
function htFireAge(data: OHLCV, target: 1 | -1, freq: HtFreq, ratio = DOJI_PCT / 100): (number | null)[] {
  const { opens, highs, lows, closes, times } = data;
  const n = closes.length;
  if (!opens || !highs || !lows || !times || times.length !== n) return new Array(n).fill(null);
  const h = resampleOHLC(times, opens, highs, lows, closes, freq);
  if (h.c.length < 3) return new Array(n).fill(null);
  const hAge = ageSeries(fireOf(computeHlcd(h.o, h.h, h.l, h.c, ratio), target), "fire");
  const hk = htIndexPerDay(times, h.t);
  return times.map((_, i) => { const k = hk[i]!; return k >= 0 && k < hAge.length ? hAge[k] ?? null : null; });
}

const HCD_GREEN = "#16A34A", LCD_RED = "#DC2626"; // high-close-doji bullish · low-close-doji bearish

// One def per concept (HCD/LCD Signal + Age) + a Timeframe select Daily / Weekly
// / Monthly (default Daily). Daily = dailyFire; Weekly/Monthly = htFire on
// ISO-week / calendar-month OHLC. Both stay instant via the tf-resolver: the
// signal derives from the baked hlcd_d / rev_w / hlcd_m reversal column; the age
// reads its baked hcd_*_age / lcd_*_age column (panelKey = Daily; Weekly/Monthly
// via TF_PRECOMPUTED).
const hlcdFire = (d: OHLCV, p: IndicatorParams, target: 1 | -1): (number | null)[] => {
  const r = ratioOf(p);
  return p[TF_PARAM] === 2 ? htFire(d, target, "month", r)
    : p[TF_PARAM] === 1 ? htFire(d, target, "week", r)
    : dailyFire(d, target, r);
};
const hlcdAge = (d: OHLCV, p: IndicatorParams, target: 1 | -1): (number | null)[] => {
  const r = ratioOf(p);
  return p[TF_PARAM] === 2 ? htFireAge(d, target, "month", r)
    : p[TF_PARAM] === 1 ? htFireAge(d, target, "week", r)
    : ageSeries(dailyFire(d, target, r) as (number | null)[], "fire");
};
const hlcdSignalDef = (id: string, label: string, target: 1 | -1, posLabel: string, fill: string): SharedIndicatorDef => ({
  id, label, group: familyGroup("hlcd"),
  kind: "fire", format: "state",
  stateLabels: { pos: posLabel, neg: "", zero: "—" }, cellColors: { pos: fill },
  params: [TF_DWM, ...HLCD_PARAMS],
  computeSeries: (d, p) => hlcdFire(d, p, target),
});
const hlcdAgeDef = (id: string, label: string, target: 1 | -1, panelKey: string): SharedIndicatorDef => ({
  id, label, group: familyGroup("hlcd"),
  kind: "age", format: "age", panelKey,
  params: [TF_DWM, ...HLCD_PARAMS],
  computeSeries: (d, p) => hlcdAge(d, p, target),
});

// Historical "-daily" ids reused as canonical; only the weekly/monthly ids alias.
export const HCD_DAILY = hlcdSignalDef("mpp-hcd-daily", "Afiat - Doji Reversal Up", 1, "Up", HCD_GREEN);
export const HCD_DAILY_AGE = hlcdAgeDef("mpp-hcd-daily-age", "Afiat - Doji Reversal Up Age", 1, "hcd_d_age");
export const LCD_DAILY = hlcdSignalDef("mpp-lcd-daily", "Afiat - Doji Reversal Down", -1, "Down", LCD_RED);
export const LCD_DAILY_AGE = hlcdAgeDef("mpp-lcd-daily-age", "Afiat - Doji Reversal Down Age", -1, "lcd_d_age");

export const HLCD_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  HCD_DAILY, HCD_DAILY_AGE, LCD_DAILY, LCD_DAILY_AGE,
];
